// Rebuilds the ArcDisk dashboard payload for a PARTIAL set of graded
// students. The fixtures hold the fully-graded dashboard; before the demo
// assessment runs nothing is graded, and while it runs students are revealed
// one by one. Everything the dashboard shows (per-skill class averages,
// mastery, the roster summaries, the disc tree) is recomputed here from the
// student × skill score matrix using the same rules as the backend's
// dashboard.service.ts, so the end state equals the live payload exactly
// (checked by __tests__/derive.test.ts).
import taxonomy from './fixtures/taxonomy.json'
import { ordinalMedian } from '../lib/discStats'
import type {
  DashboardSkillRow,
  DashboardStudent,
  GradeRow,
  ProjectDashboard,
  StudentDiscFixture,
} from './fixtures'

// ── Backend constants (dashboard.service.ts / dashboard.dao.ts) ────────────
// "Mastered" in the dashboard = score ≥ 4 (proficient). This is distinct from
// the disc's "developed" threshold (DEV_MIN = 3 in discStats.ts).
const MASTERY_MIN = 4
const NEEDS_ATTENTION_THRESHOLD = 2.5
const SKILL_STRUGGLING_THRESHOLD = 2.5
const ENCODING_K = 10
const UNGRADED_GRAY = '#cbd5e1'

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)

// The class disc encodes a skill's average mastery as ENCODING_K synthetic
// leaves: `developed` of them at level 4, the rest at level 1. DrawDisc then
// paints the skill arc with alpha = developed / total.
// The whole skill taxonomy (Area → Competency → Skill → specific skills), the
// shape Mosaico's own disc always draws in full — every area and competency,
// scored or not — captured from ArcDisk's competency_embeddings by
// tools/build-taxonomy.mjs. Area hues are ArcDisk's canonical palette slots.
type TaxonomySpecificSkill = { id: string; name: string; code: string | null }
type TaxonomySkill = { id: string; name: string; specificSkills: TaxonomySpecificSkill[] }
type TaxonomyCompetency = { id: string; name: string; children: TaxonomySkill[] }
type TaxonomyArea = { id: string; name: string; color: string; children: TaxonomyCompetency[] }
const TAXONOMY = taxonomy as TaxonomyArea[]

// Mosaico encodes a skill's class mastery as a fixed number of synthetic leaves
// (ENCODING_K): `developed` of them score 4, the rest score 1. A skill nobody
// has been graded on still gets K leaves, all scored 0 (unassessed), so it
// counts in its area's and competency's denominator exactly as Mosaico's
// unscored Lv4 items do — an area with one graded skill out of forty paints
// faintly, not fully.
function syntheticChildren(avgMastery: number | null, parentId: string) {
  if (avgMastery == null) {
    return Array.from({ length: ENCODING_K }, (_, i) => ({ id: `${parentId}__u${i}`, name: '', averageScore: 0 }))
  }
  const developed = Math.round(avgMastery * ENCODING_K)
  return [
    ...Array.from({ length: developed }, (_, i) => ({ id: `${parentId}__d${i}`, name: '', averageScore: 4 })),
    ...Array.from({ length: ENCODING_K - developed }, (_, i) => ({
      id: `${parentId}__nd${i}`,
      name: '',
      averageScore: 1,
    })),
  ]
}

function skillNode(
  id: string,
  name: string,
  nSpecificSkills: number,
  specifics: DashboardSkillRow[],
  areaName: string,
  areaColor: string,
  competencyName: string,
) {
  const graded = specifics.filter(s => s.n_graded > 0)
  const avgMastery = mean(graded.map(s => s.mastery_pct ?? 0))
  const avgClassAvg = mean(graded.map(s => s.class_avg).filter((x): x is number => x != null))
  return {
    id,
    name,
    // Hue is the AREA's (inherited down the rings, as Mosaico paints it); a
    // skill with no grades yet is drawn white.
    color: graded.length > 0 ? areaColor : UNGRADED_GRAY,
    classMeta: {
      kind: 'skill' as const,
      n_specific_skills: Math.max(nSpecificSkills, specifics.length),
      n_graded_skills: graded.length,
      avg_mastery: avgMastery,
      avg_class_avg: avgClassAvg,
      area_name: areaName,
      competency_name: competencyName,
    },
    children: syntheticChildren(avgMastery, id),
  }
}

/**
 * The class disc tree: the FULL taxonomy, with this project's skill rows
 * folded in where they land. Skills the project never mapped stay in the
 * tree unscored (white, K zero leaves) so the disc keeps Mosaico's complete
 * shape; a project row whose skill is not in the taxonomy (a fallback id)
 * is appended under its own area/competency so nothing graded is dropped.
 */
export function buildCurriculumTree(skills: DashboardSkillRow[]) {
  const bySkill = new Map<string, DashboardSkillRow[]>()
  for (const sk of skills) bySkill.set(sk.skill_id as string, [...(bySkill.get(sk.skill_id as string) ?? []), sk])
  const placed = new Set<string>()

  const areas = TAXONOMY.map(area => {
    const compNodes = area.children.map(comp => {
      const skillNodes = comp.children.map(tx => {
        const specifics = bySkill.get(tx.id) ?? []
        if (specifics.length) placed.add(tx.id)
        return skillNode(tx.id, tx.name, tx.specificSkills.length, specifics, area.name, area.color, comp.name)
      })
      return competencyNode(comp.id, comp.name, area.name, area.color, skillNodes)
    })
    return areaNode(area.id, area.name, compNodes)
  })

  // Fallback path for rows outside the taxonomy (none in the fixtures today).
  const leftovers = skills.filter(sk => !placed.has(sk.skill_id as string))
  if (leftovers.length) {
    const byArea = new Map<string, DashboardSkillRow[]>()
    for (const sk of leftovers) byArea.set(sk.area_id as string, [...(byArea.get(sk.area_id as string) ?? []), sk])
    for (const [areaId, rows] of byArea) {
      const areaName = rows[0].area ?? 'Uncategorized'
      const areaColor = rows[0].area_color
      const byComp = new Map<string, DashboardSkillRow[]>()
      for (const sk of rows) byComp.set(sk.competency_id as string, [...(byComp.get(sk.competency_id as string) ?? []), sk])
      const compNodes = [...byComp].map(([compId, compRows]) => {
        const byTheSkill = new Map<string, DashboardSkillRow[]>()
        for (const sk of compRows) byTheSkill.set(sk.skill_id as string, [...(byTheSkill.get(sk.skill_id as string) ?? []), sk])
        const compName = compRows[0].competency ?? 'Uncategorized'
        const skillNodes = [...byTheSkill].map(([skillId, specifics]) =>
          skillNode(skillId, specifics[0].skill ?? '(unnamed)', specifics.length, specifics, areaName, areaColor, compName),
        )
        return competencyNode(compId, compName, areaName, areaColor, skillNodes)
      })
      const existing = areas.find(a => a.id === areaId)
      if (existing) existing.children.push(...compNodes)
      else areas.push(areaNode(areaId, areaName, compNodes))
    }
  }
  return areas
}

type SkillNode = ReturnType<typeof skillNode>

function competencyNode(id: string, name: string, areaName: string, areaColor: string, skillNodes: SkillNode[]) {
  const graded = skillNodes.map(s => s.classMeta).filter(m => m.n_graded_skills > 0)
  return {
    id,
    name,
    color: areaColor,
    classMeta: {
      kind: 'competency' as const,
      n_skills: skillNodes.length,
      n_specific_skills: skillNodes.reduce((a, s) => a + s.classMeta.n_specific_skills, 0),
      n_graded_skills: skillNodes.reduce((a, s) => a + s.classMeta.n_graded_skills, 0),
      avg_mastery: mean(graded.map(m => m.avg_mastery ?? 0)),
      area_name: areaName,
    },
    children: skillNodes,
  }
}

type CompetencyNode = ReturnType<typeof competencyNode>

function areaNode(id: string, name: string, compNodes: CompetencyNode[]) {
  const skillMetas = compNodes.flatMap(c => c.children.map(s => s.classMeta))
  const graded = skillMetas.filter(m => m.n_graded_skills > 0)
  return {
    id,
    name,
    classMeta: {
      kind: 'area' as const,
      n_competencies: compNodes.length,
      n_skills: skillMetas.length,
      n_specific_skills: skillMetas.reduce((a, m) => a + m.n_specific_skills, 0),
      n_graded_skills: skillMetas.reduce((a, m) => a + m.n_graded_skills, 0),
      avg_mastery: mean(graded.map(m => m.avg_mastery ?? 0)),
    },
    children: compNodes,
  }
}

const round = (x: number, places: number) => Math.round(x * 10 ** places) / 10 ** places

/**
 * The dashboard as it would look if only `revealed` students had been graded.
 * `revealed` empty → the pre-assessment state (every skill ungraded, blank
 * disc); `revealed` = every student → identical to `full`.
 */
export function deriveDashboard(full: ProjectDashboard, revealed: ReadonlySet<number>): ProjectDashboard {
  const matrix = full.matrix.filter(c => revealed.has(c.student_id))
  const scoresBySkill = new Map<string, number[]>()
  const scoresByStudent = new Map<number, number[]>()
  for (const c of matrix) {
    if (c.score == null) continue
    scoresBySkill.set(c.specific_skill_id, [...(scoresBySkill.get(c.specific_skill_id) ?? []), c.score])
    scoresByStudent.set(c.student_id, [...(scoresByStudent.get(c.student_id) ?? []), c.score])
  }

  const skills: DashboardSkillRow[] = full.skills.map(s => {
    const sc = scoresBySkill.get(s.specific_skill_id) ?? []
    const nGraded = sc.length
    const nMastered = sc.filter(x => x >= MASTERY_MIN).length
    const distribution: Record<string, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    for (const x of sc) distribution[String(Math.round(x))] = (distribution[String(Math.round(x))] ?? 0) + 1
    return {
      ...s,
      class_avg: mean(sc),
      n_graded: nGraded,
      n_mastered: nMastered,
      mastery_pct: nGraded > 0 ? nMastered / nGraded : null,
      distribution,
    }
  })

  const students: DashboardStudent[] = full.students.map(st => {
    const sc = scoresByStudent.get(st.student_id) ?? []
    const avg = mean(sc)
    return {
      ...st,
      skills_graded: sc.length,
      overall_avg: avg == null ? null : round(avg, 2),
      typical_level: sc.length ? ordinalMedian(sc) : null,
    }
  })

  const gradedStudents = students.filter(s => s.overall_avg != null)
  const classAvg = mean(gradedStudents.map(s => s.overall_avg as number))
  const totalGraded = skills.reduce((a, k) => a + k.n_graded, 0)
  const totalMastered = skills.reduce((a, k) => a + k.n_mastered, 0)
  const submissionsGraded = gradedStudents.length

  return {
    meta: {
      ...full.meta,
      submissions_graded: submissionsGraded,
      submissions_ungraded: Math.max(full.meta.submissions_total - submissionsGraded, 0),
      skills_assessed: skills.filter(s => s.n_graded > 0).length,
    },
    stats: {
      ...full.stats,
      class_avg: classAvg == null ? null : round(classAvg, 1),
      class_mastery_pct: totalGraded > 0 ? totalMastered / totalGraded : null,
      students_needing_attention: gradedStudents.filter(s => (s.overall_avg ?? 0) < NEEDS_ATTENTION_THRESHOLD).length,
      skills_struggling: skills.filter(s => s.class_avg != null && s.class_avg < SKILL_STRUGGLING_THRESHOLD).length,
    },
    curriculum: buildCurriculumTree(skills),
    scores: [],
    skills,
    students,
    matrix,
  }
}

/** Grade rows for the revealed students only. */
export function deriveGrades(grades: GradeRow[], revealed: ReadonlySet<number>): GradeRow[] {
  return grades.filter(g => revealed.has(g.student_id))
}

/**
 * The "score anatomy" rows GET /api/disc/skill-evidence would return for one
 * student × specific skill: every active grade behind the disc's number,
 * with the project context the modal prints.
 */
export function deriveSkillEvidence(
  fixtures: Array<{ dashboard: ProjectDashboard; grades: GradeRow[] }>,
  studentId: number,
  skillId: string,
  revealed: ReadonlySet<number>,
) {
  if (!revealed.has(studentId)) return []
  return fixtures.flatMap(({ dashboard, grades }) =>
    grades
      .filter(g => g.student_id === studentId && g.specific_skill_id === skillId)
      .map(g => ({
        grade_id: `${g.submission_id}-${g.specific_skill_id}`,
        score_1_5: g.score_1_5,
        assessable: g.assessable,
        not_assessed_reason: g.not_assessed_reason,
        rationale: g.rationale,
        evidence_snippets: g.evidence_snippets,
        rubric_used: null,
        submission_id: g.submission_id,
        file_name: g.file_name,
        attempt_no: g.attempt_no,
        worked_at: g.created_at,
        project_id: g.project_id,
        project_title: dashboard.meta.title,
        grade_label: dashboard.meta.grade,
      }))
      .sort((a, b) => (b.attempt_no ?? 0) - (a.attempt_no ?? 0)),
  )
}

type DiscNode = {
  id?: unknown
  children?: DiscNode[]
  specificSkills?: Array<{ id: string; assessed?: boolean; score?: number }>
  [k: string]: unknown
}

/**
 * The student disc payload: the FULL taxonomy (Mosaico draws every area and
 * competency, scored or not) with this student's own assessments folded in.
 * `graded` false → the pre-assessment state: same tree, every score removed,
 * so the disc keeps its shape and is drawn all white.
 *
 * The snapshot's tree only carries the skills the student's projects mapped;
 * its assessed/score per specific skill is looked up by "<skill id>/<id>" and
 * every other descriptor in the taxonomy is present but unassessed. Each
 * descriptor's standard code comes from the taxonomy (Mosaico's curriculum
 * export), which is what the sidebar sorts by.
 */
export function deriveStudentDisc(full: StudentDiscFixture, graded: boolean): StudentDiscFixture {
  const own = new Map<string, { assessed: boolean; score: number }>()
  const collect = (node: DiscNode, depth: number) => {
    if (depth === 3) {
      for (const ss of node.specificSkills ?? []) {
        own.set(`${String(node.id)}/${ss.id}`, { assessed: !!ss.assessed, score: Number(ss.score ?? 0) })
      }
    }
    for (const c of node.children ?? []) collect(c, depth + 1)
  }
  if (graded) (full.curriculum as DiscNode[]).forEach(a => collect(a, 1))

  const leafBase = { kind: 'content', grades: [] as unknown[], currentCycle: true }
  const curriculum = TAXONOMY.map(area => ({
    id: area.id,
    name: area.name,
    color: area.color,
    ...leafBase,
    children: area.children.map(comp => ({
      id: comp.id,
      name: comp.name,
      color: area.color,
      ...leafBase,
      children: comp.children.map(skill => {
        const specificSkills = skill.specificSkills.map(ss => {
          const o = own.get(`${skill.id}/${ss.id}`)
          return { id: ss.id, name: ss.name, code: ss.code, assessed: !!o?.assessed, score: o?.score ?? 0 }
        })
        return {
          id: skill.id,
          name: skill.name,
          color: area.color,
          ...leafBase,
          specificSkills,
          children: specificSkills.map(ss => ({
            id: ss.id,
            name: ss.name,
            color: area.color,
            ...leafBase,
            assessed: ss.assessed,
            score: ss.score,
            averageScore: ss.score,
          })),
        }
      }),
    })),
  }))

  return {
    meta: graded ? full.meta : { ...full.meta, graded_skills: 0, avg_score: 0 },
    scores: [],
    curriculum,
  }
}
